'use client'

import { Button } from '@/core/ui/atoms/button'
import { FormInput } from '@/core/ui/forms/input'
import { Link, usePathname } from '@/i18n/navigation'
import { CornerUpLeft } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import { FormProvider } from 'react-hook-form'
import { useResetPassword } from './use-reset-password'

type Props = {
  onSuccess: () => void
  onClose: () => void
}

export const ResetPasswordForm: React.FC<Props> = props => {
  const t = useTranslations()

  const { form, isPending, handleSubmit } = useResetPassword(props)
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const getSigninLink = () => {
    const params = new URLSearchParams(searchParams)
    params.set('auth', 'login')
    return `${pathname}?${params.toString()}`
  }

  return (
    <div className='px-1 py-3 sm:px-10 sm:py-5'>
      <div className='mb-3 flex items-center gap-3 sm:gap-6'>
        <Link href={getSigninLink()}>
          <CornerUpLeft className='h-5 w-5 cursor-pointer text-gray-700' />
        </Link>
        <h1 className='text-mountain-bluebird text-xl font-semibold sm:text-[28px]'>
          {t('resetPassword.title')}
        </h1>
      </div>

      <p className='text-mountain-bluebird mb-6 text-base sm:text-lg'>
        {t('resetPassword.subtitle')}
      </p>

      <FormProvider {...form}>
        <form onSubmit={handleSubmit} className='space-y-4'>
          <FormInput
            name='otp_code'
            label={t('forms.codeLabel')}
            control={form.control}
          />
          <FormInput
            name='password'
            label={t('forms.newPasswordLabel')}
            placeholder={t('forms.newPasswordPH')}
            type='password'
            control={form.control}
          />

          <FormInput
            name='password_confirmation'
            label={t('forms.passwordConfirmationLabel')}
            placeholder={t('forms.passwordConfirmationPH')}
            type='password'
            control={form.control}
          />

          <Button
            loading={isPending}
            color='creamed-cron'
            className='mt-5 h-12 w-full font-semibold text-white sm:py-3'
          >
            {t('actions.save')}
          </Button>

          <Link
            href={getSigninLink()}
            className='text-cyan-blue text-sm underline sm:text-base'
          >
            {t('actions.returnToSignIn')}
          </Link>
        </form>
      </FormProvider>
    </div>
  )
}
